import Box from '@totallymoney/ui/components/Box'
import Heading from '@totallymoney/ui/components/Heading'
import Text from '@totallymoney/ui/components/Text'
import theme from '@totallymoney/ui/theme'

import formatNumberToCurrency from '@utils/formatNumberToCurrency'

import {
    GetUnscoredCategoriesFilter,
    UnscoredLoanProduct,
} from '@appTypes/LoansApi.interface'

import BannerOffer from '@components/BannerOffer'
import BannerStack from '@components/BannerStack'
import LoanTableContentBase from '@components/LoansTableContentBase'
import LoanTableImage from '@components/LoansTableImage'
import LoanTableItemProductDetail from '@components/LoansTableItemProductDetail'
import RepresentativeExample from '@components/RepresentativeExample'
import TooltipButton from '@components/TooltipButton'

import * as Layout from './components/UnscoredTableItemLayout'

interface UnscoredTableItemProps {
    filter: GetUnscoredCategoriesFilter,
    item: UnscoredLoanProduct,
    onShowLoanTypeModal: () => void,
    onShowRepresentativeExampleModal: () => void,
    renderButtons: (item: UnscoredLoanProduct) => React.ReactNode
}

const UnscoredTableItem = ({
    item,
    filter,
    onShowRepresentativeExampleModal,
    onShowLoanTypeModal,
    renderButtons,
}: UnscoredTableItemProps) => {
    return (
        <>
            {item.hasOffer &&
            item.offerTitle != null &&
            item.offerTitle.toUpperCase() !== 'GUARANTEED RATE' ? (
                <BannerStack>
                    <BannerOffer
                        title={item.offerTitle}
                        description={item.offerDescription}
                        backgroundColor={
                            item.exclusiveLoan ? theme.indigo30 : '#E4FCFF'
                        }
                        variant="backdrop"
                    />
                </BannerStack>
            ) : null}
            <Layout.ItemBase id={item.propositionId}>
                <Layout.ItemWrapper>
                    <Layout.ItemHeading>
                        <Box width="112px" mr={theme.space20}>
                            <LoanTableImage
                                cmsImageUrl={item.imageUrl}
                                name={item.lender}
                                isPortraitImage={false}
                            />
                        </Box>
                        <Box>
                            <Heading as="h2" variant="60">
                                {item.lender}
                            </Heading>
                            <Box display="flex" alignItems="center">
                                <Text
                                    variant="120"
                                    style={{
                                        fontFamily: theme.buenosAires,
                                        color: theme.grey80,
                                    }}
                                >
                                    {item.loanName}
                                </Text>
                                <Box
                                    ml={theme.space10}
                                    display="inline-flex"
                                    alignItems="center"
                                >
                                    <TooltipButton
                                        title={`${item.propositionId}-tooltip`}
                                        aria-label={`What is a ${item.loanName}?`}
                                        size={18}
                                        color={theme.grey80}
                                        onPress={onShowLoanTypeModal}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Layout.ItemHeading>

                    <Layout.ItemContent>
                        <LoanTableContentBase>
                            <LoanTableItemProductDetail
                                title="Representative APR (fixed)"
                                value={`${item.apr}% APR`}
                            />
                            <LoanTableItemProductDetail
                                title="Monthly repayment"
                                value={formatNumberToCurrency(
                                    item.monthlyPayment,
                                    {
                                        includeIntegerFractionDigits: true,
                                    }
                                )}
                            />
                            <LoanTableItemProductDetail
                                title="Total charge for credit"
                                value={formatNumberToCurrency(
                                    item.totalCharge,
                                    {
                                        includeIntegerFractionDigits: true,
                                    }
                                )}
                            />
                            <LoanTableItemProductDetail
                                title="Total amount repayable"
                                value={formatNumberToCurrency(
                                    item.totalRepayable,
                                    {
                                        includeIntegerFractionDigits: true,
                                    }
                                )}
                            />
                        </LoanTableContentBase>
                    </Layout.ItemContent>

                    <Layout.ItemButtons>
                        {renderButtons(item)}
                    </Layout.ItemButtons>
                </Layout.ItemWrapper>

                <Layout.ItemFooter>
                    <RepresentativeExample
                        item={item}
                        loanAmount={filter.loanAmount}
                        loanTerm={filter.loanTerm}
                        propositionId={item.propositionId}
                        isScored={false}
                        onTooltipPress={onShowRepresentativeExampleModal}
                    />
                </Layout.ItemFooter>
            </Layout.ItemBase>
        </>
    )
}

export default UnscoredTableItem
